import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, CalendarClock, CheckCircle2, Sparkles, AlertCircle, Share2, Users } from "lucide-react";
import { RESULTS, CATEGORY_LABEL } from "@/data/quiz";
import ScoreBar from "@/components/ScoreBar";

const reflectionPrompts = [
  "I want more\u2026",
  "That matters to me because\u2026",
  "One small change I will test in the next fortnight is\u2026",
];

export default function Results({ result, settings, onRestart }) {
  const leads = result.leading || [];
  const tied = leads.length > 1;
  const emailSent = result.email_sent;
  const name = result.first_name;

  const [copied, setCopied] = useState(false);
  const leadWords = leads.map((c) => CATEGORY_LABEL[c].toLowerCase()).join(" & ");
  const shareText = `My Working Life reflection with The Weave Founder Academy: I want more ${leadWords}. Explore what you want more of:`;
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";

  const handleShare = async () => {
    const data = { title: "The Weave \u2014 Your Working Life", text: shareText, url: shareUrl };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success("Summary copied to your clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn\u2019t copy \u2014 please copy the text manually.");
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16" data-testid="results-page">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {emailSent ? (
          <div className="flex items-start gap-3 rounded-xl bg-[#eef6f6] border border-[#4aa0a4]/30 p-4 mb-8" data-testid="email-confirmation">
            <CheckCircle2 className="h-5 w-5 text-[#4aa0a4] mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed text-[#1e292b]">
              {name ? `Thank you, ${name}. ` : "Thank you. "}
              We’ve emailed your results and the Personal Clarity Prospectus. Do check your inbox (and
              spam folder, just in case).
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-xl bg-[#fdf7e7] border border-[#f4b847]/40 p-4 mb-8" data-testid="email-pending">
            <AlertCircle className="h-5 w-5 text-[#9e7016] mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed text-[#1e292b]">
              Your results are ready below. We weren’t able to send your email just now, but your results
              are saved and we’ll deliver your prospectus by email shortly.
            </p>
          </div>
        )}

        <p className="text-base text-[#5a686b]">
          {tied
            ? "You have more than one leading priority. Explore how these needs could work together in your next chapter."
            : "Your answers suggest you may want more of\u2026"}
        </p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-semibold tracking-tight leading-tight text-[#1d4447]" data-testid="results-headline">
          {leads.map((c) => RESULTS[c].heading.replace(/^More /, "")).join(" & ")}
        </h1>

        <div className="mt-8 rounded-2xl bg-white border border-[#e2ded7] p-6 sm:p-8 shadow-sm">
          <ScoreBar scores={result.scores} />
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[#5a686b] italic">
          This quiz is a reflection tool. Your results describe your choices today, rather than a fixed
          personality type or a recommendation to leave your job.
        </p>

        <div className="mt-8 space-y-5">
          {leads.map((cat) => {
            const r = RESULTS[cat];
            return (
              <div key={cat} className="rounded-2xl bg-[#eef6f6] border border-[#4aa0a4]/20 p-6 sm:p-8" data-testid={`result-card-${cat}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs uppercase tracking-widest font-semibold text-[#4aa0a4]">
                    {CATEGORY_LABEL[cat]}
                  </span>
                </div>
                <h3 className="text-2xl font-semibold text-[#1d4447]">{r.heading}</h3>
                <p className="mt-3 text-base leading-relaxed text-[#1e292b]">{r.body}</p>
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-white/70 p-4">
                    <p className="text-xs uppercase tracking-widest font-semibold text-[#5a686b] mb-1">Reflection</p>
                    <p className="text-sm leading-relaxed text-[#1e292b]">{r.reflection}</p>
                  </div>
                  <div className="rounded-xl bg-white/70 p-4">
                    <p className="text-xs uppercase tracking-widest font-semibold text-[#5a686b] mb-1">A small next step</p>
                    <p className="text-sm leading-relaxed text-[#1e292b]">{r.nextStep}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reflection prompts */}
        <div className="mt-8 rounded-2xl bg-white border border-[#e2ded7] p-6 sm:p-8">
          <h3 className="text-xl font-medium text-[#1e292b]">A little space to think</h3>
          <ul className="mt-4 space-y-4">
            {reflectionPrompts.map((p, i) => (
              <li key={i} className="border-l-2 border-[#f4b847] pl-4 text-base text-[#1e292b]" data-testid={`reflection-prompt-${i}`}>
                {p}
              </li>
            ))}
          </ul>
        </div>

        {/* Prospectus + Academy intro */}
        <div className="mt-8 rounded-2xl bg-[#1d4447] text-white p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-[#f4b847]" />
            <span className="text-xs uppercase tracking-widest font-semibold text-[#f4b847]">The Weave Founder Academy</span>
          </div>
          <p className="text-base leading-relaxed text-white/90">
            The Weave Founder Academy puts the founder first. Through practical learning, reflection and a
            supportive community, we help people explore how their experience could shape a meaningful
            business. Personal Clarity is the starting point: understanding what matters to you, what you
            bring and what you want your next chapter to make possible.
          </p>
          {settings.prospectus_url ? (
            <a
              href={settings.prospectus_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prospectus-download-button"
              className="mt-5 inline-flex items-center gap-2 bg-[#f4b847] text-[#1e292b] font-medium px-6 py-3 rounded-full hover:bg-[#e6a835] transition-all shadow-md active:scale-[0.98]"
            >
              <Download className="h-4 w-4" />
              Download the Personal Clarity Prospectus
            </a>
          ) : (
            <p className="mt-5 text-sm text-white/70" data-testid="prospectus-unavailable">
              The prospectus will be available here shortly. We’ve emailed you as soon as it’s ready.
            </p>
          )}
        </div>

        {/* Book a conversation */}
        <div className="mt-8 rounded-2xl bg-[#fdf7e7] border border-[#f4b847]/40 p-6 sm:p-8" data-testid="booking-section">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="h-5 w-5 text-[#9e7016]" />
            <h3 className="text-2xl font-semibold text-[#1e292b]">Would a conversation help?</h3>
          </div>
          <p className="text-base leading-relaxed text-[#1e292b]">
            Book a relaxed, 20-minute call with James Cracknell, co-founder of The Weave. Explore the
            purpose of the Founder Academy, ask questions about Personal Clarity and discuss whether the
            approach feels right for your next chapter. There’s no obligation to join.
          </p>
          {settings.booking_url ? (
            <a
              href={settings.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-button"
              className="mt-5 inline-flex items-center gap-2 bg-[#4aa0a4] text-white font-medium px-6 py-3 rounded-full hover:bg-[#3b868a] transition-all shadow-md active:scale-[0.98]"
            >
              <CalendarClock className="h-4 w-4" />
              Book a 20-minute conversation
            </a>
          ) : (
            <p className="mt-5 text-sm text-[#9e7016]" data-testid="booking-unavailable">
              Booking opens shortly — we’ll include the link in your email.
            </p>
          )}
        </div>

        {/* Join the Academy waitlist */}
        <div className="mt-8 rounded-2xl bg-[#1d4447] text-white p-6 sm:p-8" data-testid="waitlist-section">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-[#f4b847]" />
            <h3 className="text-2xl font-semibold">Join The Academy waitlist</h3>
          </div>
          <p className="text-base leading-relaxed text-white/90">
            Be the first in the know and make 2027 the year of clarity.
          </p>
          {settings.waitlist_url ? (
            <a
              href={settings.waitlist_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="waitlist-button"
              className="mt-5 inline-flex items-center gap-2 bg-[#e93e36] text-white font-medium px-6 py-3 rounded-full hover:bg-[#d2352e] transition-all shadow-md active:scale-[0.98]"
            >
              <Users className="h-4 w-4" />
              Join The Academy Waitlist
            </a>
          ) : (
            <p className="mt-5 text-sm text-white/70" data-testid="waitlist-unavailable">
              The waitlist opens shortly — we’ll let you know.
            </p>
          )}
        </div>

        {/* Shareable summary card */}
        <div className="mt-8 rounded-2xl bg-white border border-[#e2ded7] p-6 sm:p-8" data-testid="share-card">
          <h3 className="text-xl font-medium text-[#1e292b]">Share your reflection</h3>
          <p className="mt-1 text-sm leading-relaxed text-[#5a686b]">
            A tasteful summary of what you want more of — to keep, or to share with someone who might
            find the quiz helpful too.
          </p>
          <div className="mt-4 rounded-xl bg-gradient-to-br from-[#eef6f6] to-[#fdf7e7] border border-[#4aa0a4]/20 p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-[#4aa0a4]">Your Working Life</p>
            <p className="mt-2 text-2xl font-semibold text-[#1d4447] capitalize" data-testid="share-card-priority">
              I want more {leadWords}
            </p>
            <p className="mt-3 text-sm text-[#5a686b]">The Weave Founder Academy · Founder First; Business Second.</p>
          </div>
          <button
            onClick={handleShare}
            data-testid="share-button"
            className="mt-5 inline-flex items-center gap-2 bg-white text-[#1e292b] font-medium px-6 py-3 rounded-full border-2 border-[#e2ded7] hover:border-[#4aa0a4] hover:bg-[#faf8f5] transition-all active:scale-[0.98]"
          >
            <Share2 className="h-4 w-4 text-[#4aa0a4]" />
            {copied ? "Copied" : "Share my result"}
          </button>
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={onRestart}
            data-testid="results-restart-button"
            className="text-sm font-medium text-[#5a686b] hover:text-[#1e292b] underline underline-offset-4 transition-colors"
          >
            Take the quiz again
          </button>
        </div>
      </motion.div>
    </main>
  );
}
